/*
 * The checkout (prototype .rgrid, .coMain, .rail, .coSec): the steps on the
 * left, the summary held still on the right. In the telify layer.
 */
@layer telify {
  .grid {
    display: grid;
    grid-template-columns: minmax(0, 680px) 360px;
    justify-content: space-between;
    gap: 64px;
    align-items: start;
    padding: 36px 0 0;
  }

  .main {
    padding-bottom: 72px;
  }

  .back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14.5px;
    color: var(--text-tertiary);
    margin-bottom: 18px;
  }

  .h1 {
    font-family: var(--font-display);
    font-weight: 500;
    letter-spacing: -0.015em;
    line-height: 1.05;
    font-size: 42px;
  }

  .steps {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .sec {
    margin-top: 30px;
    padding-top: 26px;
    border-top: var(--line);
  }

  .secHead {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .num {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 13px;
    font-weight: 600;
    border: 1px solid var(--field-line);
    color: var(--text-secondary);
    flex-shrink: 0;
  }

  .num svg {
    width: 15px;
    height: 15px;
    stroke-width: 2.4;
  }

  .active .num {
    background: var(--text-primary);
    color: var(--bg-page);
    border-color: var(--text-primary);
  }

  .done .num {
    border-color: var(--accent);
    color: var(--accent);
  }

  .secTitle {
    font-size: 19px;
    font-weight: 600;
  }

  .waiting .secTitle {
    color: var(--text-muted);
  }

  .body {
    margin-top: 20px;
    padding-left: 42px;
  }

  .signed {
    margin-top: 12px;
    padding-left: 42px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 15px;
  }

  .signed > span:last-child {
    color: var(--text-tertiary);
    font-size: 13.5px;
  }

  .avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: var(--avatar-bg);
    color: var(--avatar-initials-color);
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.02em;
    flex-shrink: 0;
  }

  .problem {
    font-size: 15px;
    color: var(--text-primary);
  }

  .openings {
    margin-top: 16px;
    display: grid;
    gap: 16px;
  }

  .dayLabel {
    font-size: 13.5px;
    font-weight: 500;
    color: var(--text-secondary);
  }

  .times {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .time {
    appearance: none;
    cursor: pointer;
    height: 42px;
    padding: 0 16px;
    border-radius: var(--r-ctl);
    border: 1px solid var(--field-line);
    background: transparent;
    color: var(--text-primary);
    font: inherit;
    font-size: 15px;
    font-variant-numeric: tabular-nums;
  }

  /* The steps' bottom space under the summary too, so a summary taller than the steps never meets the footer. */
  .rail {
    position: sticky;
    top: calc(var(--head-h) + 36px);
    margin-bottom: 72px;
    padding-left: 40px;
    border-left: var(--line);
  }

  .photo {
    height: 170px;
    border-radius: 12px;
    overflow: hidden;
  }

  .item {
    margin-top: 18px;
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 18px;
    border-bottom: var(--line);
  }

  .itemName {
    font-family: var(--font-display);
    font-size: 26px;
    font-weight: 500;
    line-height: 1.1;
  }

  .itemSub {
    margin-top: 5px;
    font-size: 14px;
    color: var(--text-tertiary);
  }

  .itemPrice {
    font-size: 16px;
    font-weight: 500;
    padding-top: 4px;
    white-space: nowrap;
  }

  .rows {
    margin: 0;
    padding: 6px 0;
    border-bottom: var(--line);
  }

  .rows > div {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 0;
    font-size: 14.5px;
  }

  .rows dt {
    color: var(--text-tertiary);
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .rows dt svg {
    color: var(--text-muted);
  }

  .rows dd {
    margin: 0;
    text-align: right;
  }

  .total {
    display: flex;
    justify-content: space-between;
    padding: 18px 0 0;
    font-size: 16.5px;
    font-weight: 600;
  }

  .total small {
    display: block;
    font-size: 13px;
    font-weight: 400;
    color: var(--text-tertiary);
    margin-top: 3px;
  }

  .hold {
    margin-top: 18px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: var(--text-secondary);
  }

  .hold svg {
    color: var(--accent);
  }

  /* service-by-phone D5 (the drawing's B frames): the number to call under the rows, in the hold line's style,
     with the shop checkout's Change link (ShopCheckout .textlink); Change opens the website Profile's own field
     and buttons (Profile .on .k, .input, .btns; account .btnSm). */
  .callLine {
    flex-wrap: wrap;
  }

  .textlink {
    flex: none;
    font-size: 15px;
    color: var(--accent);
    background: none;
    border: 0;
    border-bottom: 1px solid rgba(var(--accent-rgb), 0.4);
    padding: 0 0 3px;
    cursor: pointer;
  }

  .phoneField {
    display: grid;
    gap: 8px;
    margin-top: 16px;
  }

  .phoneLabelled {
    display: grid;
    gap: 8px;
  }

  .phoneLabel {
    font-size: 14.5px;
    color: var(--accent);
  }

  .phoneInput {
    max-width: 460px;
    height: 48px;
    border-radius: 8px;
    border: 1px solid rgba(var(--accent-rgb), 0.6);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.12);
    background: rgba(var(--fg-page-rgb), 0.03);
    display: flex;
    align-items: center;
    padding: 0 14px;
    font-size: 15.5px;
  }

  .phoneInput input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    font: inherit;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
  }

  .phoneHint {
    margin-top: 0;
  }

  .phoneBtns {
    display: flex;
    gap: 12px;
  }

  .phoneBtn {
    height: 44px;
    padding: 0 18px;
    font-size: 15px;
    font-weight: 500;
  }

  .phoneBtn:disabled {
    background: transparent;
    color: var(--text-muted);
    border-color: var(--border-soft);
    cursor: default;
  }

  @media (max-width: 1023px) {
    .grid {
      grid-template-columns: minmax(0, 1fr);
      gap: 28px;
    }

    .rail {
      position: static;
      padding-left: 0;
      border-left: 0;
    }
  }

  /* web-studio-sites D12: a custom site's narrow checkout (bookings.html .rgrid, .coRail, .coMain and .tw-page at 1024
     pixels, .coSecBody at 860): one column from 1024 pixels, and the page's bottom space comes after the summary,
     not between the steps and it. */
  @media (max-width: 1024px) {
    :global(:root[data-custom-site]) .main {
      padding-bottom: 24px;
    }

    :global(:root[data-custom-site]) .grid {
      grid-template-columns: minmax(0, 1fr);
      gap: 28px;
      padding-bottom: 110px;
    }

    :global(:root[data-custom-site]) .rail {
      position: static;
      margin-bottom: 0;
      padding-left: 0;
      border-left: 0;
    }
  }

  @media (max-width: 860px) {
    :global(:root[data-custom-site]) .h1 {
      font-size: 34px;
    }

    :global(:root[data-custom-site]) .body,
    :global(:root[data-custom-site]) .signed {
      padding-left: 0;
    }
  }
}
